import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import toast from "react-hot-toast";
import axios from "axios";
import {
  Palette,
  Save,
  Image,
  Phone,
  MapPin,
  Building,
  Eye,
} from "lucide-react";

const API = `${window.location.origin}/api`;

export default function WhiteLabelSettingsPage() {
  const { user, isWhiteLabel, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    app_name: "",
    logo_url: "",
    primary_color: "#10b981",
    secondary_color: "#0d9488",
    contact_phone: "",
    contact_address: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/auth/whitelabel-settings`);
      setSettings(response.data);
    } catch (error) {
      // استخدم الإعدادات الافتراضية
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/whitelabel-settings`, settings);
      toast.success("تم حفظ الإعدادات بنجاح");
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في الحفظ");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (!isWhiteLabel) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="bg-slate-800/50 border-slate-700 max-w-md">
          <CardContent className="p-8 text-center">
            <Palette className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              ميزة غير متاحة
            </h2>
            <p className="text-slate-400">
              إعدادات العلامة التجارية متاحة فقط لترخيص White Label
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="whitelabel-settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600">
          <Palette className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">
            إعدادات العلامة التجارية
          </h1>
          <p className="text-slate-400">
            خصص شكل التطبيق ليناسب علامتك التجارية
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building className="w-5 h-5 text-purple-500" />
              معلومات العلامة التجارية
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-200">اسم التطبيق / الشركة</Label>
              <Input
                value={settings.app_name}
                onChange={(e) => handleChange("app_name", e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="مثال: محلات أبو علي"
                data-testid="app-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Image className="w-4 h-4" />
                رابط الشعار (Logo URL)
              </Label>
              <Input
                value={settings.logo_url}
                onChange={(e) => handleChange("logo_url", e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="https://example.com/logo.png"
                dir="ltr"
                data-testid="logo-url-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-200">اللون الأساسي</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => handleChange("primary_color", e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.primary_color}
                    onChange={(e) => handleChange("primary_color", e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white font-mono"
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">اللون الثانوي</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.secondary_color}
                    onChange={(e) => handleChange("secondary_color", e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.secondary_color}
                    onChange={(e) => handleChange("secondary_color", e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white font-mono"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                رقم التواصل
              </Label>
              <Input
                value={settings.contact_phone}
                onChange={(e) => handleChange("contact_phone", e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="07701234567"
                dir="ltr"
                data-testid="contact-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                العنوان
              </Label>
              <Input
                value={settings.contact_address}
                onChange={(e) => handleChange("contact_address", e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="بغداد - الكرادة"
                data-testid="contact-address-input"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="save-settings-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري الحفظ...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  حفظ الإعدادات
                </span>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-500" />
              معاينة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-900 rounded-xl p-6 space-y-4">
              {/* Preview Header */}
              <div className="flex items-center gap-3 pb-4 border-b border-slate-700">
                {settings.logo_url ? (
                  <img 
                    src={settings.logo_url} 
                    alt="Logo" 
                    className="w-12 h-12 rounded-xl object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    {settings.app_name?.charAt(0) || "H"}
                  </div>
                )}
                <div>
                  <h3 className="text-white font-bold">
                    {settings.app_name || "نازاريو حساباتي"}
                  </h3>
                  <p className="text-slate-400 text-sm">نظام إدارة المتاجر</p>
                </div>
              </div>

              {/* Preview Buttons */}
              <div className="space-y-2">
                <button 
                  className="w-full py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  زر أساسي
                </button>
                <button 
                  className="w-full py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: settings.secondary_color }}
                >
                  زر ثانوي
                </button>
              </div>

              {/* Preview Contact */}
              {(settings.contact_phone || settings.contact_address) && (
                <div className="pt-4 border-t border-slate-700 space-y-1 text-sm">
                  {settings.contact_phone && (
                    <p className="text-slate-400">
                      <Phone className="w-3 h-3 inline me-1" />
                      {settings.contact_phone}
                    </p>
                  )}
                  {settings.contact_address && (
                    <p className="text-slate-400">
                      <MapPin className="w-3 h-3 inline me-1" />
                      {settings.contact_address}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
